import type { BootstrapResponse, HealthResponse, Problem } from "@ppx/shared-types";
import type {
  ApiKeyItem,
  ApiKeysResponse,
  CreatedApiKey,
  MeResponse,
  MfaChallengeResponse,
  PermissionsResponse,
  SessionsResponse,
  TokenResponse,
} from "./types";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/v1";

const AUTH_FREE_PATHS = [
  "/auth/token",
  "/auth/mfa/verify",
  "/auth/refresh",
  "/bootstrap",
  "/health",
];

let accessToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;
let refreshInFlight: Promise<string | null> | null = null;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  readonly problem: Problem | null;

  constructor(status: number, code: string, detail: string, problem: Problem | null) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.problem = problem;
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

function notifyUnauthorized(): void {
  accessToken = null;
  if (unauthorizedHandler) {
    unauthorizedHandler();
  }
}

function isAuthFree(path: string): boolean {
  return AUTH_FREE_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function applyTokens(data: TokenResponse | MfaChallengeResponse): string | null {
  if (typeof data.access_token === "string" && data.access_token !== "") {
    accessToken = data.access_token;
    return accessToken;
  }
  return null;
}

async function performRefresh(): Promise<string | null> {
  try {
    const response = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as TokenResponse;
    return applyTokens(data);
  } catch {
    return null;
  }
}

export function tryRefresh(): Promise<string | null> {
  if (refreshInFlight !== null) {
    return refreshInFlight;
  }
  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function toApiError(response: Response): Promise<ApiError> {
  let problem: Problem | null = null;
  try {
    problem = (await response.json()) as Problem;
  } catch {
    problem = null;
  }
  const code =
    problem !== null && typeof problem.code === "string" && problem.code !== ""
      ? problem.code
      : `http_${response.status}`;
  const detail =
    problem !== null && typeof problem.detail === "string" && problem.detail !== ""
      ? problem.detail
      : response.statusText !== ""
        ? response.statusText
        : `HTTP ${response.status}`;
  return new ApiError(response.status, code, detail, problem);
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
  allowRefresh?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true, allowRefresh = true } = options;

  const doFetch = (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (auth && accessToken !== null) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return fetch(`${BASE}${path}`, {
      method,
      credentials: "include",
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  let response = await doFetch();

  if (response.status === 401 && allowRefresh && !isAuthFree(path)) {
    const token = await tryRefresh();
    if (token !== null) {
      response = await doFetch();
    } else {
      notifyUnauthorized();
    }
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  if (text === "") {
    return undefined as unknown as T;
  }

  return JSON.parse(text) as T;
}

export function fetchBootstrap(): Promise<BootstrapResponse> {
  return request<BootstrapResponse>("/bootstrap", { auth: false, allowRefresh: false });
}

export function fetchHealth(): Promise<HealthResponse> {
  return request<HealthResponse>("/health", { auth: false, allowRefresh: false });
}

export function login(email: string, password: string, deviceId?: string): Promise<TokenResponse> {
  return request<TokenResponse>("/auth/token", {
    method: "POST",
    auth: false,
    allowRefresh: false,
    body: { email, password, device_id: deviceId },
  }).then((data) => {
    applyTokens(data);
    return data;
  });
}

export function verifyMfa(mfaToken: string, code: string): Promise<TokenResponse> {
  return request<TokenResponse>("/auth/mfa/verify", {
    method: "POST",
    auth: false,
    allowRefresh: false,
    body: { mfa_token: mfaToken, code },
  }).then((data) => {
    applyTokens(data);
    return data;
  });
}

export function mfaChallenge(code: string): Promise<MfaChallengeResponse> {
  return request<MfaChallengeResponse>("/auth/mfa/challenge", {
    method: "POST",
    body: { code },
  }).then((data) => {
    applyTokens(data);
    return data;
  });
}

export function logout(allSessions = false): Promise<void> {
  return request<void>("/auth/logout", {
    method: "POST",
    allowRefresh: false,
    body: { all_sessions: allSessions },
  });
}

export function fetchMe(): Promise<MeResponse> {
  return request<MeResponse>("/auth/me");
}

export function fetchPermissions(): Promise<PermissionsResponse> {
  return request<PermissionsResponse>("/me/permissions");
}

export function fetchSessions(): Promise<SessionsResponse> {
  return request<SessionsResponse>("/auth/sessions");
}

export function revokeSession(id: string): Promise<void> {
  return request<void>(`/auth/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function listApiKeys(): Promise<ApiKeysResponse> {
  return request<ApiKeysResponse>("/api-keys");
}

export function createApiKey(input: {
  name: string;
  scopes: string[];
  expiresInDays: number | null;
}): Promise<CreatedApiKey> {
  return request<CreatedApiKey>("/api-keys", {
    method: "POST",
    body: {
      name: input.name,
      scopes: input.scopes,
      expires_in_days: input.expiresInDays,
    },
  });
}

export function revokeApiKey(id: string): Promise<void> {
  return request<void>(`/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export type { ApiKeyItem };
